// Profit On Your Terms's home (POYT.dc.html, its "Paid in full" Tweak), worked out from the product's
// own rows (src/product/data.js, with POYT's extra columns: columns.js columnsFor) and the app's
// clock. Pure: no network and no React, so a test can import it as it is.
//
// The board draws the week before the course: Cohort 1 starts on Wednesday 21 October, Deal
// analysis is done and Marketing setup isn't. The rest of a member's 6 weeks follow the same rules:
//
// - hero: their intake and when it starts ("Live cohort 1", "Starts Wednesday 21 October", the
//   open page's line, "21 days to Week 1", "Add all 6 to your calendar" and the join note); once the
//   intake has started, the next live session instead ("Week 2 is live tonight", Join on Zoom from
//   join_opens_minutes before until it ends). Sessions come back only for the intakes they belong to
//   (row level security), so a resit's 2 intakes both count.
// - start: Start here's 2 rows of screens (Adam, 5 Oct: the 19 interactive screens, opened from the
//   board's 2 rows). "1 of 2 done" counts the rows the database has marked done. A row reads Start
//   with none of its screens touched, Continue part way and "Go back through it" once done
//   (settings.start_here.buttons). The screens open in order across both rows (the database's gate),
//   so a row whose first screen isn't open yet waits for the row before it.
// - tool: the two-offer analyser (settings.tool_card), its page in the hub, in a new tab, where the
//   tool runner runs it (model.js toolHref). Only while that placement is open to them.
// - help: "Questions between sessions" with the product's questions address (settings.questions).
// - weeks: "Your 6 weeks", each with its intake's date, its title, its pack line and where it is.
// - guarantee and surgery: the scripts guarantee, and "Your deal surgery" only for an intake with
//   the deal surgery switched on, which they hold open (active or overdue).
import * as f from '../../lib/format.js'
import { isReleased, moduleRecordings, numberedModules, progressFor, sessionForModule, toolHref } from '../../product/model.js'

const MIN = 60e3
const httpsLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)
const bySort = (a, b) => (a.sort_order - b.sort_order) || String(a.id).localeCompare(String(b.id))
// the Library's kind line for a live course (library/model.js kindLine, which a test can't import)
const kindLine = p => (p?.type === 'live_cohort' ? 'Live course' : p?.name || '')
const titleOf = m => (m.title && !f.isPlaceholder(m.title) ? m.title : m.label)

// {email} in the product's own words, filled with its questions address
export function fillEmail(text, email) {
  if (typeof text !== 'string' || !text) return ''
  return email ? text.replaceAll('{email}', email) : text
}

// Their live sessions, soonest first, with each one's end
function liveSessions(data) {
  return (data.sessions || []).filter(s => s.status !== 'cancelled' && s.starts_at)
    .map(s => ({ ...s, startMs: Date.parse(s.starts_at), endMs: Date.parse(s.starts_at) + (s.duration_minutes || 0) * MIN }))
    .sort((a, b) => a.startMs - b.startMs)
}

// A session's moment: live, Join open, days away, evening (the Library's rule, library/model.js)
export function momentOf(session, product, nowMs) {
  const start = Date.parse(session.starts_at)
  const end = start + (session.duration_minutes || 0) * MIN
  const joinAt = start - (product.join_opens_minutes ?? 15) * MIN
  return {
    start, end, joinAt,
    live: start <= nowMs && nowMs < end,
    ended: end <= nowMs,
    joinOpen: nowMs >= joinAt && nowMs < end,
    days: f.daysFrom(nowMs, start),
    evening: f.uk(start).hh >= 17,
  }
}

// The name a session goes by: its week's label ("Week 2"), else its own title
function sessionLabel(data, session) {
  const mod = session.module_id ? (data.modules || []).find(m => m.id === session.module_id) : null
  return mod?.label || session.title
}

// Their intake: the 1 a session belongs to, else the latest of theirs
function intakeOf(data, session) {
  const list = data.cohorts || []
  if (session?.cohort_id) {
    const c = list.find(x => x.id === session.cohort_id)
    if (c) return c
  }
  return list.slice().sort((a, b) => String(b.starts_on || '').localeCompare(String(a.starts_on || '')))[0] || null
}

// "Add all 6 to your calendar", "Add the 5 dates to your calendar", "Add Week 6 to your calendar"
export function calendarLabel(count, weeksTotal, firstTitle) {
  if (count >= 2) return weeksTotal && count === weeksTotal ? `Add all ${count} to your calendar` : `Add the ${count} dates to your calendar`
  return `Add ${firstTitle} to your calendar`
}

// The hero: { eyebrow, title: { lead, mark }, line, card: { big, label, join, calendar, note } | null }
export function heroFor(data, product, nowMs) {
  const page = product.settings?.open_page || {}
  const sessions = liveSessions(data)
  const upcoming = sessions.filter(s => s.endMs > nowMs)
  const next = upcoming[0] || null
  const intake = intakeOf(data, next)
  const eyebrow = intake ? `Live ${intake.name.toLowerCase()}` : kindLine(product)
  const before = sessions.length > 0 && nowMs < sessions[0].startMs
  const startsOn = intake?.starts_on ? f.calendarDay(intake.starts_on) : null
  // their intake hasn't started: the board's hero
  const ahead = Boolean(startsOn && startsOn.day > f.uk(nowMs).day)

  let title
  let line
  if (ahead) {
    title = { lead: `Starts ${f.weekday(startsOn)} `, mark: f.dayMonthLong(startsOn) }
    line = page.hero_line || null
  } else if (next) {
    const m = momentOf(next, product, nowMs)
    const label = sessionLabel(data, next)
    let lead
    if (m.live) lead = `${label} is live now`
    else if (m.days === 0) lead = `${label} is live ${m.evening ? 'tonight' : 'today'}`
    else if (m.days === 1) lead = `${label} is live tomorrow`
    else if (m.days < 7) lead = `${label} is live on ${f.weekday(m.start)}`
    else lead = `${label} is live on ${f.dayMonthLong(m.start)}`
    title = { lead, mark: null }
    line = before
      ? page.hero_line || null
      : `${intake?.schedule_label ? `${intake.schedule_label} on Zoom` : 'Live on Zoom'}. Each week's recording, notes and pack go up here after the session.`
  } else if (sessions.length) {
    title = { lead: product.name, mark: null }
    line = "Every week's recording, notes and pack are here for you."
  } else {
    title = { lead: product.name, mark: null }
    line = 'Your session dates show here once Adam adds you to an intake.'
  }

  let card = null
  if (next) {
    const m = momentOf(next, product, nowMs)
    const label = sessionLabel(data, next)
    let big
    let small
    if (m.live) { big = 'Live'; small = `until ${f.time(m.end)}` }
    else if (m.days <= 0) { big = f.time(m.start); small = `${label}, ${m.evening ? 'tonight' : 'today'}` }
    else { big = String(m.days); small = `${m.days === 1 ? 'day' : 'days'} to ${label}` }
    const zoom = httpsLink(next.zoom_join_url)
    const join = (m.live || m.joinOpen) && zoom ? { href: zoom, label: 'Join on Zoom', aria: `Join on Zoom: ${product.name}, ${label}` } : null
    const toAdd = upcoming.filter(s => s.startMs > nowMs)
    const calendar = toAdd.length ? {
      label: calendarLabel(toAdd.length, intake?.weeks_total, sessionLabel(data, toAdd[0])),
      events: toAdd.map(s => ({
        uid: s.id, title: `${product.name}, ${sessionLabel(data, s)}`, startMs: s.startMs,
        endMs: s.endMs > s.startMs ? s.endMs : s.startMs + 60 * MIN, path: product.path || '/',
      })),
    } : null
    card = { big, label: small, join, calendar, note: join ? null : page.join_note || null }
  }
  return { eyebrow, title, line, card }
}

// ---- Start here

// Start here's rows, each with its screens and where they are in them:
// { count, doneCount, footnote, rows: [{ id, key, num, title, body, done, state, button, waitLine }] }
// state: 'start' | 'continue' | 'done' | 'locked'; button: { label, href } (none while locked)
export function startHere(data, product) {
  const set = product.settings?.start_here || {}
  const words = { start: 'Start', continue: 'Continue', done: 'Go back through it', ...(set.buttons || {}) }
  const steps = data.steps || []
  const rows = steps.filter(s => s.section === 'start_here' && !s.parent_step_id).sort(bySort)
  if (!rows.length) return null
  const screensOf = row => steps.filter(s => s.parent_step_id === row.id && s.step_type === 'screen').sort(bySort)
  const progress = new Map((data.stepProgress || []).map(p => [p.step_id, p]))
  const doneAt = s => Boolean(progress.get(s.id)?.done_at)
  // the database's gate: the screens open in order across the rows, the first or 1 past the
  // furthest done
  const order = rows.flatMap(screensOf)
  let furthest = 0
  order.forEach((s, i) => { if (doneAt(s)) furthest = i + 1 })
  const base = `${product.path || ''}/start-here`

  const list = rows.map((row, i) => {
    const screens = screensOf(row)
    const done = doneAt(row) || (screens.length > 0 && screens.every(doneAt))
    const touched = screens.some(s => progress.has(s.id))
    const firstPos = screens.length ? order.indexOf(screens[0]) + 1 : 0
    let state
    if (done) state = 'done'
    else if (touched) state = 'continue'
    else if (screens.length && firstPos > furthest + 1) state = 'locked'
    else state = 'start'
    const first = screens[0] || null
    const next = screens.find(s => !doneAt(s)) || first
    const target = state === 'continue' ? next : first
    const href = target ? `${base}/${row.step_key}/${target.step_key}` : `${base}/${row.step_key}`
    const before = rows[i - 1]
    return {
      id: row.id, key: row.step_key, num: String(i + 1), title: row.title, body: row.body_md || '',
      done, state,
      button: state === 'locked' ? null : { label: words[state], href },
      waitLine: state === 'locked' && before ? `Opens after ${before.title}` : null,
    }
  })
  const doneCount = list.filter(r => r.done).length
  return { count: `${doneCount} of ${list.length} done`, doneCount, rows: list, footnote: set.footnote || null }
}

// ---- The tool card

// The board's tool card, or null when its placement isn't open to them here
export function toolCardFor(data, product) {
  const set = product.settings?.tool_card
  if (!set?.placement_id) return null
  const pl = (data.placements || []).find(p => p.id === set.placement_id && !p.removed_at)
  if (!pl || data.open?.[pl.id] !== true) return null
  const item = (data.items || []).find(i => i.id === pl.item_id)
  if (!item || item.kind !== 'tool') return null
  return {
    eyebrow: set.eyebrow || 'Tool',
    title: set.title || pl.title_override || item.title,
    line: set.line || pl.subline_override || item.subline || '',
    button: set.button || 'Open',
    href: toolHref(product, pl.id),
  }
}

// ---- The 6 weeks

const watchedRec = (data, r) => Boolean(progressFor(data, r.id)?.watched_at)

// "Your 6 weeks": { heading, note, rows: [{ id, label, date, title, pack, status, tone, current, done, href }] }
// tone: 'next' (the week being taught next, in orange), 'live', 'done', 'carry', 'soft'
export function weekRows(data, product, nowMs) {
  const path = product.path || ''
  const mods = numberedModules(data)
  // the first week whose session hasn't ended is the 1 being taught next
  const nextId = mods.find(m => {
    const s = sessionForModule(data, m.id)
    return s && Date.parse(s.starts_at) + (s.duration_minutes || 0) * MIN > nowMs
  })?.id || null
  const rows = mods.map(m => {
    const s = sessionForModule(data, m.id)
    const recs = moduleRecordings(data, m.id)
    const open = recs.filter(r => isReleased(data, r.id))
    const row = {
      id: m.id, key: m.key, label: m.label,
      date: s ? `${f.shortDate(s.starts_at)}, ${f.time(s.starts_at)}` : '',
      title: titleOf(m), pack: m.pack_line ? `Pack: ${m.pack_line}` : null,
      status: '', tone: 'soft', current: m.id === nextId, done: false, href: null,
    }
    if (open.length) {
      row.href = `${path}/${m.key}`
      const r = open.find(x => !watchedRec(data, x)) || open[0]
      const pos = progressFor(data, r.id)?.position_seconds || 0
      if (open.every(x => watchedRec(data, x))) { row.status = 'Watched'; row.tone = 'done'; row.done = true }
      else if (pos > 10) { row.status = `Carry on from ${f.clock(pos)}`; row.tone = 'carry' }
      else { row.status = 'Not started'; row.tone = 'soft' }
      return row
    }
    if (!s) { row.status = 'Opens after the live session'; return row }
    const mo = momentOf(s, product, nowMs)
    if (mo.ended) { row.status = 'The recording goes up after the session'; return row }
    if (m.id !== nextId) { row.status = 'Opens after the session'; return row }
    row.tone = 'next'
    if (mo.live) { row.status = 'Live now'; row.tone = 'live' }
    else if (mo.joinOpen) { row.status = 'Join is open'; row.tone = 'live' }
    else if (mo.days === 0) row.status = `Live ${mo.evening ? 'tonight' : 'today'} at ${f.time(mo.start)}`
    else if (mo.days === 1) row.status = 'Live tomorrow'
    else row.status = `Live in ${mo.days} days`
    return row
  })
  const n = rows.length
  return {
    heading: `Your ${n} ${n === 1 ? 'week' : 'weeks'}`,
    note: "Each week's recording, notes and pack go up here after the session",
    rows,
  }
}

// ---- The deal surgery

// The intake of theirs with the deal surgery on (Cohort 1), held open (active or overdue), or null
// owned: the Library's ownership of the product (library/model.js ownedProducts), every access row
export function surgeryIntake(data, owned) {
  const open = (owned?.rows || [owned?.access].filter(Boolean))
    .filter(r => ['active', 'overdue'].includes(r.status) && r.cohort_name)
  return (data.cohorts || []).find(c => c.deal_surgery && open.some(r => r.cohort_name === c.name)) || null
}

// The words on the 4 cards from the product's settings (POYT.dc.html's, seeded), with {email}
export function cardWords(product) {
  const s = product.settings || {}
  const email = product.questions_email || null
  const q = s.questions || {}
  const g = s.scripts_guarantee || {}
  const d = s.deal_surgery || {}
  return {
    email,
    help: email ? { title: q.title || 'Questions between sessions', line: fillEmail(q.line || 'Email {email}. Adam answers them himself.', email) } : null,
    guarantee: g.title && g.body ? { title: g.title, body: g.body, link: email && g.link ? fillEmail(g.link, email) : null } : null,
    surgery: d.title && d.body ? {
      title: d.title, body: d.body, button: d.button || 'Book your 30 minutes', done: d.done || 'Adam will send you a time',
    } : null,
  }
}

// A line with the questions address in it, split so the address can be a link:
// ['Email ', 'course@adamslee.co.uk', '. Adam answers them himself.']
export function splitEmail(text, email) {
  if (!email || !text) return [text || '']
  const at = text.indexOf(email)
  if (at < 0) return [text]
  return [text.slice(0, at), email, text.slice(at + email.length)]
}
